/**
 * OPFS Wrapper for iOS Compatibility
 * Simple wrapper API that routes through workers on Safari
 */

import debugx from "debugx";
// @ts-ignore - webpack will handle this import
import OPFSWorker from "./opfs-polyfill.worker";

const debug = debugx("bikedash:opfs");

// Detect Safari (all Safari - iOS and desktop - needs workers)
// Safari doesn't support async createWritable() properly, only createSyncAccessHandle() in workers
function isSafari(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  const isSafariDesktop = /^((?!chrome|android).)*safari/i.test(ua);
  return isIOS || isSafariDesktop;
}

const USE_WORKERS = isSafari();

debug(
  "Safari detected: %s - workers %s",
  USE_WORKERS,
  USE_WORKERS ? "enabled" : "disabled",
);

// Worker pool
const workers: Worker[] = [];
let currentWorker = 0;
const pendingRequests = new Map<
  string,
  { resolve: Function; reject: Function }
>();
let requestCounter = 0;
// Session affinity: map file paths to worker index to ensure same worker handles file operations
const pathToWorkerMap = new Map<string, number>();

if (USE_WORKERS) {
  debug(
    "Initializing single worker for Safari (OPFS sync access handles use exclusive locks)",
  );

  // @ts-ignore
  const worker = new OPFSWorker();

  worker.addEventListener("message", (event: any) => {
    const { id, success, result, error } = event.data;

    const pending = pendingRequests.get(id);
    if (pending) {
      pendingRequests.delete(id);
      if (success) {
        pending.resolve(result);
      } else {
        debug.error("Request failed: %s - %s", id, error);
        pending.reject(new Error(error || "Unknown error"));
      }
    } else {
      debug.warn("Response for unknown request: %s", id);
    }
  });

  worker.addEventListener("error", (error: any) => {
    debug.error("Worker error: %o", error);
  });

  workers.push(worker);
  debug("Worker initialized");
}

function sendToWorker(
  method: string,
  path: string[],
  args?: any[],
): Promise<any> {
  const id = `req_${requestCounter++}_${Date.now()}`;
  const request = { id, method, path, args };

  return new Promise((resolve, reject) => {
    pendingRequests.set(id, { resolve, reject });

    // Single worker - no need for round-robin or affinity
    const worker = workers[0];
    worker.postMessage(request);

    setTimeout(() => {
      if (pendingRequests.has(id)) {
        pendingRequests.delete(id);
        debug.error("Request timeout: %s", method);
        reject(new Error("Request timeout"));
      }
    }, 30000);
  });
}

// Wrapper classes
export class OPFSWritableFileStream {
  private writerId?: string;
  private nativeWritable?: FileSystemWritableFileStream;
  private path: string[];

  constructor(
    writerId: string | undefined,
    nativeWritable: FileSystemWritableFileStream | undefined,
    path: string[],
  ) {
    this.writerId = writerId;
    this.nativeWritable = nativeWritable;
    this.path = path;
  }

  async write(data: string | ArrayBuffer | Uint8Array): Promise<void> {
    if (USE_WORKERS && this.writerId) {
      await sendToWorker("write", this.path, [this.writerId, data]);
    } else if (this.nativeWritable) {
      // @ts-ignore - TypeScript type narrowing issue with Uint8Array
      await this.nativeWritable.write(data);
    }
  }

  async seek(position: number): Promise<void> {
    if (USE_WORKERS && this.writerId) {
      await sendToWorker("seek", this.path, [this.writerId, position]);
    } else if (this.nativeWritable) {
      await this.nativeWritable.seek(position);
    }
  }

  async close(): Promise<void> {
    if (USE_WORKERS && this.writerId) {
      await sendToWorker("close", this.path, [this.writerId]);
    } else if (this.nativeWritable) {
      await this.nativeWritable.close();
    }
  }
}

export class OPFSFileHandle {
  readonly kind = "file";
  private path: string[];
  private nativeHandle?: FileSystemFileHandle;

  constructor(path: string[], nativeHandle?: FileSystemFileHandle) {
    this.path = path;
    this.nativeHandle = nativeHandle;
  }

  get name(): string {
    return this.path[this.path.length - 1];
  }

  async createWritable(options?: {
    keepExistingData?: boolean;
  }): Promise<OPFSWritableFileStream> {
    if (USE_WORKERS) {
      const result = await sendToWorker("createWritable", this.path, [options]);
      return new OPFSWritableFileStream(result.writerId, undefined, this.path);
    } else {
      const writable = await this.nativeHandle!.createWritable(options);
      return new OPFSWritableFileStream(undefined, writable, this.path);
    }
  }

  async getFile(): Promise<File> {
    if (USE_WORKERS) {
      const result = await sendToWorker("getFile", this.path);
      // Create a File-like object
      return {
        text: async () => result.text,
        size: result.size,
      } as any;
    } else {
      return await this.nativeHandle!.getFile();
    }
  }
}

export class OPFSDirectoryHandle {
  readonly kind = "directory";
  private path: string[];
  private nativeHandle?: FileSystemDirectoryHandle;

  constructor(path: string[], nativeHandle?: FileSystemDirectoryHandle) {
    this.path = path;
    this.nativeHandle = nativeHandle;
  }

  get name(): string {
    return this.path[this.path.length - 1] || "";
  }

  async getDirectoryHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<OPFSDirectoryHandle> {
    if (USE_WORKERS) {
      await sendToWorker("getDirectoryHandle", this.path, [name, options]);
      return new OPFSDirectoryHandle([...this.path, name], undefined);
    } else {
      const handle = await this.nativeHandle!.getDirectoryHandle(name, options);
      return new OPFSDirectoryHandle([...this.path, name], handle);
    }
  }

  async getFileHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<OPFSFileHandle> {
    if (USE_WORKERS) {
      await sendToWorker("getFileHandle", this.path, [name, options]);
      return new OPFSFileHandle([...this.path, name], undefined);
    } else {
      const handle = await this.nativeHandle!.getFileHandle(name, options);
      return new OPFSFileHandle([...this.path, name], handle);
    }
  }

  async removeEntry(
    name: string,
    options?: { recursive?: boolean },
  ): Promise<void> {
    if (USE_WORKERS) {
      await sendToWorker("removeEntry", this.path, [name, options]);
    } else {
      await this.nativeHandle!.removeEntry(name, options);
    }
  }

  async *values(): AsyncIterableIterator<OPFSDirectoryHandle | OPFSFileHandle> {
    if (USE_WORKERS) {
      const result = await sendToWorker("values", this.path);
      for (const entry of result.entries) {
        if (entry.kind === "directory") {
          yield new OPFSDirectoryHandle([...this.path, entry.name], undefined);
        } else {
          yield new OPFSFileHandle([...this.path, entry.name], undefined);
        }
      }
    } else {
      // @ts-ignore
      for await (const entry of this.nativeHandle!.values()) {
        if (entry.kind === "directory") {
          yield new OPFSDirectoryHandle([...this.path, entry.name], entry);
        } else {
          yield new OPFSFileHandle([...this.path, entry.name], entry);
        }
      }
    }
  }
}

/**
 * Get OPFS root directory
 * Use this instead of navigator.storage.getDirectory()
 */
export async function getOPFSRoot(): Promise<OPFSDirectoryHandle> {
  if (USE_WORKERS) {
    await sendToWorker("getDirectory", []);
    return new OPFSDirectoryHandle([], undefined);
  } else {
    const root = await navigator.storage.getDirectory();
    return new OPFSDirectoryHandle([], root);
  }
}
